/**
 * Unified Story Templates
 *
 * All templates follow SINGULAR-FIRST design to prevent AI hallucinations:
 * - Main text describes ONE character/costume/action
 * - Multi-character variations isolated in "FOR MULTIPLE CHARACTERS:" sections
 * - No plural triggers: "all characters", "each", "together", "team", "formation"
 * - Scene verbs are neutral: "soaring" works for 1 or 3 characters
 */

/**
 * Occasion ids reuse the slugs of the landing pages in content/occasions/*.ts
 * (birthday, new-sibling, grandparents, christmas, diwali, mothers-day, fathers-day)
 * so there is one vocabulary; 'bedtime' and 'first-day-of-school' have no landing page yet.
 */
export type StoryOccasion =
  | 'birthday' | 'new-sibling' | 'grandparents' | 'bedtime' | 'first-day-of-school'
  | 'christmas' | 'diwali' | 'mothers-day' | 'fathers-day';

/** Occasion groups, in the order the story step shows them. */
export const STORY_OCCASIONS: { id: StoryOccasion; label: string }[] = [
  { id: 'birthday', label: 'Birthday' },
  { id: 'new-sibling', label: 'New baby in the family' },
  { id: 'grandparents', label: 'For grandparents' },
  { id: 'bedtime', label: 'Bedtime' },
  { id: 'first-day-of-school', label: 'First day of school' },
  { id: 'christmas', label: 'Christmas & winter holidays' },
  { id: 'diwali', label: 'Diwali' },
  { id: 'mothers-day', label: "Mother's Day" },
  { id: 'fathers-day', label: "Father's Day" },
];

export interface UnifiedStoryTemplate {
  id: string;
  category: 'sports' | 'science' | 'fantasy' | 'occasions' | 'educational' | 'adventure' | 'animals' | 'mystery' | 'learning';
  titleSingular: string; // Use [[NAME]] as placeholder
  titlePlural: string;   // Use [[NAMES]] as placeholder
  description: string;
  thumbnailUrl: string;
  themeId: string;
  styleId: string;
  ageRange: { min: number; max: number };
  defaultPageCount: number;
  storyContext: {
    setting: string;
    plotOutline: string;
    additionalComments: string;
  };
  hasHazardousEnvironments?: boolean;
  hazardOutfitDescription?: string;
  hasSuperPowers?: boolean;
  /** Occasion landing-page slug (content/occasions/*.ts) for occasion templates. */
  occasion?: StoryOccasion;

  // Conflict & Dramatic Arc fields
  conflictType?: 'villain' | 'environmental' | 'competition' | 'mystery' | 'internal' | 'discovery';
  antagonist?: {
    type: 'named_villain' | 'force_of_nature' | 'challenge' | 'rival';
    name?: string;           // e.g., "Dr. Shadow"
    description: string;     // e.g., "a villain who controls darkness"
    threat: string;          // e.g., "covering the city in eternal night"
  };
}

export const UNIFIED_STORY_TEMPLATES: UnifiedStoryTemplate[] = [
  // ============================================
  // FANTASY/ADVENTURE TEMPLATES
  // ============================================
  {
    id: 'superheroes',
    category: 'fantasy',
    titleSingular: "[[NAME]] the Superhero",
    titlePlural: "[[NAMES]] the Superheroes",
    description: "Become a superhero with amazing powers, saving the city from danger!",
    thumbnailUrl: "/images/templates/superheroes.jpg",
    themeId: "superheroes",
    styleId: "modern3d",
    ageRange: { min: 3, max: 10 },
    defaultPageCount: 12,
    storyContext: {
      setting: "A gleaming metropolis with towering skyscrapers and a secret superhero headquarters, now under threat from the villainous Dr. Shadow",
      plotOutline: "Discovers amazing superpowers, learns to control them, and battles the evil Dr. Shadow who is covering the city in eternal darkness with his shadow creatures",
      additionalComments: `COSTUME: Sleek superhero suit in bold red with golden accents, flowing cape, eye mask with glow, star emblem on chest, utility belt, boots with energy glow. Cape clasps in gold.
FOR MULTIPLE CHARACTERS: Different colors - red, blue, gold, purple, silver.

VISUAL POWER: Glowing cosmic energy from hands - beams, shields, flight aura. Energy crackles and trails dramatically.

VILLAIN: Dr. Shadow - dark cloaked figure with glowing purple eyes, commands shadow creatures. His presence dims light.

EPIC SCENES: Costume reveal with light burst, soaring above city skyline, rooftop landing with cape settling, powers activating, battling Dr. Shadow, victory pose at sunset.`
    },
    hasSuperPowers: true,
    conflictType: 'villain',
    antagonist: {
      type: 'named_villain',
      name: 'Dr. Shadow',
      description: 'a mysterious villain who controls darkness and shadow creatures',
      threat: 'covering the city in eternal darkness and unleashing shadow monsters'
    }
  },

  {
    id: 'storm-giant',
    category: 'fantasy',
    titleSingular: "[[NAME]] and the Storm Giant",
    titlePlural: "[[NAMES]] and the Storm Giant",
    description: "A night-time storm, a giant made of cloud and a power that is yours alone: fly over your own town and save it.",
    thumbnailUrl: "/images/templates/superheroes.jpg",
    themeId: "superheroes",
    styleId: "modern3d",
    ageRange: { min: 6, max: 10 },
    defaultPageCount: 16,
    storyContext: {
      setting: "The hero's own town at night under a huge storm, from the rooftops and bridges to the tram line and the harbour",
      plotOutline: "A power arrives early and is the hero's own, with one limit that the climax tests. A giant made of storm cloud threatens the whole town, the hero flies to meet it, there are rescues along the way, and a hard choice at the climax decides how the storm ends. The family helps from the ground. Everyone is safe at the end.",
      additionalComments: `COSTUME: An own-design superhero suit in bold colours with a cape, boots and a chest emblem. No mask over the face: the face is always fully visible and recognisable. The cast keep their own skin tone and features on every page.
FOR MULTIPLE CHARACTERS: Each hero wears a different colour of the same suit.

VISUAL POWER: Flight with a glowing trail, plus one signature ability that shows as bright energy. Energy crackles and trails dramatically.

VILLAIN: A giant made of storm cloud, with a face and a want. Fought cartoon-style: big and loud, nobody is hurt on the page.

EPIC SCENES: First flight over the hero's own town at night, a rescue high above the street, the eye of the storm, the power failing, a comeback with the family on the ground, calm skies at dawn.`,
    },
    hasSuperPowers: true,
    conflictType: 'villain',
    antagonist: {
      type: 'named_villain',
      description: 'a giant made of storm cloud with a face of its own and something it wants',
      threat: 'a storm that could sweep over the whole town',
    },
  },

  {
    id: 'dragon-riders',
    category: 'fantasy',
    titleSingular: "[[NAME]] the Dragon Rider",
    titlePlural: "[[NAMES]] the Dragon Riders",
    description: "Bond with a magnificent dragon and soar through magical skies!",
    thumbnailUrl: "/images/templates/dragon-riders.jpg",
    themeId: "fantasy",
    styleId: "modern3d",
    ageRange: { min: 4, max: 10 },
    defaultPageCount: 12,
    storyContext: {
      setting: "A magnificent dragon academy on floating islands above the clouds",
      plotOutline: "Bonds with a dragon hatchling, trains at the academy, and proves worthy as a true dragon rider",
      additionalComments: `COSTUME: Dragon rider leather armor in rich crimson red, dragon-scale shoulder guards with metallic sheen, leather flight goggles pushed up on forehead, flowing crimson cloak that billows dramatically in wind, dragon-tooth pendant necklace, leather gloves with reinforced grip pads, sturdy boots with dragon-scale trim. Armor color matches dragon.
FOR MULTIPLE CHARACTERS: Same armor style, different colors - first in crimson, second in sapphire blue, third in emerald green, fourth in royal purple, fifth in bronze. Each rider's armor matches their dragon's color.

DRAGON COMPANION: Bonds with a magnificent crimson dragon with powerful scaled body, iridescent sheen, massive leathery wings spanning wide, intelligent golden eyes, curved horns, and the ability to breathe brilliant flames in matching color. Dragon is loyal, expressive, shows clear bond with rider through nuzzling and protective posture.
FOR MULTIPLE CHARACTERS: Each has a dragon in their matching color (red, blue, green, purple, bronze). All dragons breathe flames in their own color.

EPIC SCENES: First magical meeting with dragon hatchling showing instant bond, dragon choosing ceremony with ancient elders watching, learning to saddle and mount the growing dragon, nervous first flight with wobbly takeoff, soaring confidently through clouds above floating islands, breathtaking aerial loop and dive maneuvers, dragon breathing spectacular flames, defending kingdom from dramatic sky dive, triumphant return to floating academy.

ENVIRONMENT: Magnificent dragon academy on floating islands above clouds, crystal spire towers catching sunlight, ancient stone dragon statues, volcanic mountain nesting grounds with warm glow, endless sky with dramatic cloud formations, golden hour lighting at sunrise and sunset.

TONE: Deep emotional bond between rider and dragon. Wonder at flight, determination in training, triumphant mastery. Dragon is intelligent partner.`
    },
    hasHazardousEnvironments: true,
    hazardOutfitDescription: "dragon rider leather armor with flight goggles",
    conflictType: 'villain',
    antagonist: {
      type: 'named_villain',
      name: 'The Shadow Hunters',
      description: 'dark riders who capture and cage dragons for their power',
      threat: 'enslaving dragons and destroying the academy'
    }
  },

  {
    id: 'save-the-planet',
    category: 'fantasy',
    titleSingular: "[[NAME]] Saves the Planet!",
    titlePlural: "[[NAMES]] Save the Planet!",
    description: "Transform into alien royalty and save a glowing alien moon!",
    thumbnailUrl: "/images/templates/save-the-planet.jpg",
    themeId: "fantasy",
    styleId: "modern3d",
    ageRange: { min: 5, max: 12 },
    // 16 = what the wizard's `defaultPageCount || 16` fallback gave this template before the field existed.
    defaultPageCount: 16,
    storyContext: {
      setting: "A glowing alien moon with floating mountains, massive bioluminescent trees hundreds of feet tall, forests that glow blue and purple at night, floating jellyfish-like creatures in the air, and crystalline rock formations",
      plotOutline: "Child is transformed into blue-skinned alien royalty (prince if male, princess if female) and learns to navigate an alien world, bonds with a magnificent flying dragon, learns to ride it through the floating mountains, and has a rip-roaring adventure saving the planet from danger",
      additionalComments: `CRITICAL BODY TRANSFORMATION - Character is transformed into blue-skinned alien royalty throughout the ENTIRE book. Use "alien prince" for male characters, "alien princess" for female characters, "alien warriors" for multiple characters. Body features on EVERY page: bright blue skin, large golden cat-like eyes, pointed elf-like ears, a flat wide nose, glowing bioluminescent striations on cheeks and arms, and a long blue prehensile tail that is ALWAYS visible (curving behind, wrapped around a branch, or swishing to the side). COSTUME: wears a tribal alien outfit - a woven top made of natural fibers and beads in earthy browns and greens, a short leaf-like bottom, bare feet, and decorative beaded necklaces and arm bands. NO Earth clothing. The character is NEVER shown in human form or without alien features. IMPORTANT: Focus on story action and dialogue, not on describing the character's appearance in the story text.`
    },
    hasHazardousEnvironments: false
  },

  {
    id: 'wizard-school',
    category: 'fantasy',
    titleSingular: "[[NAME]] at Wizard School",
    titlePlural: "[[NAMES]] at Wizard School",
    description: "Learn magical spells at an enchanted academy of wizardry!",
    thumbnailUrl: "/images/templates/wizard-school.jpg",
    themeId: "fantasy",
    styleId: "modern3d",
    ageRange: { min: 4, max: 10 },
    defaultPageCount: 12,
    storyContext: {
      setting: "An ancient magical academy with moving staircases and enchanted classrooms",
      plotOutline: "Receives acceptance letter, learns spells, and masters magic to save the day",
      additionalComments: `COSTUME: Flowing wizard robe in deep midnight blue with silver star embroidery, pointed wizard hat with matching stars, golden clasp at collar, leather spell book satchel at hip, polished wooden wand with crystal core held ready. Robe hem shows hints of purple lining.
FOR MULTIPLE CHARACTERS: Same robe style, different house colors - first in midnight blue, second in emerald green, third in crimson red, fourth in golden yellow, fifth in royal purple. Each has matching hat and trim color.

VISUAL MAGIC: Casts spells with wand producing brilliant golden sparkles and swirling magical energy. Spells manifest as glowing runes, floating objects surrounded by shimmer, transformation effects with spiral particles. Wand tip glows when casting.

EPIC SCENES: Receiving magical acceptance letter delivered by owl, first awestruck view of towering castle academy, sorting ceremony with magical artifact, levitating feather in first class with concentrated expression, brewing colorful potion with bubbling cauldron, flying lesson on enchanted broomstick, discovering secret passage behind painting, casting perfect spell with triumphant smile, graduation ceremony with diploma scroll.

ENVIRONMENT: Grand castle academy with floating candles, moving portraits, spiral staircases that shift, library with books that fly, greenhouse with magical plants, potion dungeon with glowing ingredients, great hall with enchanted ceiling showing sky.

TONE: Wonder at magical world. Determination to learn and improve. Friendship and adventure. Triumphant magical mastery.`
    },
    hasSuperPowers: true,
    conflictType: 'internal',
    antagonist: {
      type: 'challenge',
      description: 'a magical mishap that threatens the school',
      threat: 'wild magic spreading through the academy that only a brave student can stop'
    }
  },

  {
    id: 'magic-adventure',
    category: 'fantasy',
    titleSingular: "[[NAME]]'s Magic Adventure",
    titlePlural: "[[NAMES]]'s Magic Adventure",
    description: "Discover magical powers and embark on an enchanted quest!",
    thumbnailUrl: "/images/templates/magic-adventure.jpg",
    themeId: "fantasy",
    styleId: "modern3d",
    ageRange: { min: 3, max: 8 },
    defaultPageCount: 10,
    storyContext: {
      setting: "An enchanted forest realm where magic is real and creatures are friendly",
      plotOutline: "Discovers innate magical ability, meets magical creatures, and completes an important quest",
      additionalComments: `COSTUME: Enchanted adventurer outfit with flowing teal cloak that shimmers with hidden magic, leather vest with silver leaf clasps, comfortable traveling boots with vine patterns, woven satchel for magical items, glowing crystal pendant that reveals magic. Hair adorned with small flowers that appear naturally.
FOR MULTIPLE CHARACTERS: Same outfit style, different cloak colors - first in teal, second in lavender, third in golden amber, fourth in rose pink, fifth in forest green. Each pendant glows in matching color.

VISUAL MAGIC: Innate nature magic manifests as gentle glowing light from hands, ability to communicate with forest creatures shown by swirling nature sparkles, healing touch shown by golden shimmer. Magic appears soft and natural, not aggressive.

EPIC SCENES: Discovery of magical ability with surprised delight, first conversation with talking woodland creature, receiving quest from wise ancient tree, crossing enchanted bridge over sparkling stream, helping lost fairy find way home, solving riddle to open magical door, finding enchanted artifact glowing with power, returning triumphantly to grateful magical community, celebration with forest creatures dancing.

ENVIRONMENT: Enchanted forest with oversized colorful mushrooms, trees with friendly faces, sparkling streams with lily pads, hidden fairy villages in flowers, glowing fireflies, rainbow waterfalls, cozy forest clearings with soft moss.

TONE: Gentle wonder and discovery. Kindness to magical creatures. Bravery balanced with gentleness. Warm, magical atmosphere throughout.`
    },
    hasSuperPowers: true
  },

  {
    id: 'fairyland-journey',
    category: 'fantasy',
    titleSingular: "[[NAME]]'s Fairyland Journey",
    titlePlural: "[[NAMES]]'s Fairyland Journey",
    description: "Shrink down to fairy size and explore a magical tiny world!",
    thumbnailUrl: "/images/templates/fairyland.jpg",
    themeId: "fantasy",
    styleId: "modern3d",
    ageRange: { min: 3, max: 7 },
    defaultPageCount: 10,
    storyContext: {
      setting: "A magical fairyland hidden among flowers and mushrooms in a garden",
      plotOutline: "Magically shrinks to fairy size, befriends fairies, and helps save their tiny kingdom",
      additionalComments: `COSTUME: Delicate fairy outfit with gossamer wings that shimmer with rainbow iridescence, flower petal dress or tunic in soft pink, tiny crown of dewdrops and flower buds, barefoot or soft petal slippers. Wings have intricate vein patterns that catch light.
FOR MULTIPLE CHARACTERS: Same fairy outfit style, different flower colors - first in soft pink, second in sky blue, third in sunny yellow, fourth in lavender, fifth in peach. Wings have matching tint.

VISUAL MAGIC: Fairy dust sprinkles from fingertips in sparkling trails, wings glow softly when flying, can make flowers bloom with gentle touch. Magic is always gentle, sparkly, and nature-connected.

EPIC SCENES: Magical shrinking sequence with swirling sparkles, first amazed look at giant garden from tiny perspective, meeting friendly fairy guide, riding on butterfly through garden, feast on dewdrop and pollen treats, exploring mushroom village with tiny houses, flying for first time with wings spread, helping fairy friends with important task, grand fairy celebration with dancing lights.

ENVIRONMENT: Garden seen from tiny perspective - enormous flowers like buildings, mushrooms as houses, dewdrops like giant jewels, grass blades like trees, acorn cap furniture, spider silk decorations (friendly), firefly lanterns, petal boats on puddle lakes.

TONE: Gentle wonder and magic. Everything is beautiful and friendly. Sense of tiny adventure in big world. Celebration of nature's small miracles.`
    },
    hasSuperPowers: true
  },

  {
    id: 'pirate-adventure',
    category: 'adventure',
    titleSingular: "[[NAME]] the Pirate",
    titlePlural: "[[NAMES]] the Pirates",
    description: "Set sail on the high seas searching for buried treasure!",
    thumbnailUrl: "/images/templates/pirate.jpg",
    themeId: "adventure",
    styleId: "modern3d",
    ageRange: { min: 3, max: 10 },
    defaultPageCount: 12,
    storyContext: {
      setting: "The high seas with tropical islands, hidden coves, and buried treasure",
      plotOutline: "Becomes a pirate captain, sails the seas, solves clues, and discovers legendary treasure",
      additionalComments: `COSTUME: Classic pirate captain outfit with red bandana tied on head, puffy white shirt with wide sleeves, black vest with golden buttons, brown leather belt with ornate buckle, dark pants tucked into tall brown boots, dramatic red sash at waist. Optional eye patch for extra flair. Always has spyglass or treasure map in hand.
FOR MULTIPLE CHARACTERS: Same pirate style, different bandana/sash colors - first in red, second in blue, third in green, fourth in purple, fifth in gold. Each has matching color accents.

PIRATE GEAR: Ornate captain's spyglass with brass fittings, weathered treasure map with X marks the spot, compass with spinning needle, rope and grappling hook, trusty cutlass in belt (kept sheathed for kid-friendly).

EPIC SCENES: Dramatic ship's wheel steering through waves, studying treasure map with excited expression, looking through spyglass at distant island, swinging on rope between ships, landing on beach with tropical palms, following map through jungle, discovering treasure chest half-buried, opening chest to golden glow on face, celebrating with treasure held high.

ENVIRONMENT: Magnificent tall ship with billowing sails, ocean waves with sparkles, tropical islands with palm trees, mysterious caves with stalactites, sandy beaches with coconuts, jungle paths with ancient ruins, treasure cave with golden glow.

TONE: Exciting adventure and discovery. Clever puzzle-solving with the map. Triumphant treasure discovery. Fun and swashbuckling spirit throughout.`
    },
    hasHazardousEnvironments: true,
    hazardOutfitDescription: "pirate outfit with bandana and vest",
    conflictType: 'villain',
    antagonist: {
      type: 'named_villain',
      name: 'Captain Blackheart',
      description: 'a rival pirate captain with a fearsome crew',
      threat: 'racing to find the treasure first and keeping it all'
    }
  },

  {
    id: 'time-travel',
    category: 'adventure',
    titleSingular: "[[NAME]]'s Time Travel Adventure",
    titlePlural: "[[NAMES]]'s Time Travel Adventure",
    description: "Journey through time visiting amazing historical eras!",
    thumbnailUrl: "/images/templates/time-travel.jpg",
    themeId: "adventure",
    styleId: "modern3d",
    ageRange: { min: 4, max: 10 },
    defaultPageCount: 12,
    storyContext: {
      setting: "Multiple time periods accessed through a magical time machine",
      plotOutline: "Discovers a time machine, visits incredible historical moments, and returns home with wisdom",
      additionalComments: `COSTUME: Time traveler explorer outfit with khaki adventure jacket covered in pockets, utility belt with gadgets, sturdy boots, aviator goggles pushed up on forehead, glowing wrist device that controls time travel (shows swirling vortex pattern when active). Jacket has timeline patches.
FOR MULTIPLE CHARACTERS: Same explorer outfit, different jacket colors - first in khaki, second in navy blue, third in forest green, fourth in burgundy, fifth in charcoal gray. Each has matching wrist device.

TIME MACHINE: Spectacular brass and copper time machine with spinning gears, glowing power core, comfortable seats, control panel with dates and levers. Creates swirling blue time vortex when activating.

EPIC SCENES: Discovery of hidden time machine with amazed expression, first activation with swirling vortex forming, arriving in dinosaur era with T-rex visible, visiting ancient Egypt with pyramids building, meeting medieval knights at castle, watching rocket launch in space era, returning home with timeline safely restored, showing treasured souvenir from journey.

ENVIRONMENT: Time vortex tunnel with swirling colors, prehistoric jungle with dinosaurs, ancient Egypt with pyramids and sphinx, medieval castle with knights, future city with flying cars, home setting for departure and return.

TONE: Wonder at seeing history firsthand. Excitement of time travel. Learning through adventure. Appreciation for home upon return.`
    },
    hasHazardousEnvironments: true,
    hazardOutfitDescription: "time traveler outfit with aviator goggles and utility jacket"
  },

  {
    id: 'jungle-explorer',
    category: 'adventure',
    titleSingular: "[[NAME]] the Jungle Explorer",
    titlePlural: "[[NAMES]] the Jungle Explorers",
    description: "Explore the rainforest and discover amazing wildlife!",
    thumbnailUrl: "/images/templates/jungle.jpg",
    themeId: "nature",
    styleId: "modern3d",
    ageRange: { min: 3, max: 8 },
    defaultPageCount: 10,
    storyContext: {
      setting: "A vibrant tropical rainforest teeming with exotic wildlife",
      plotOutline: "Embarks on jungle expedition, befriends animals, and discovers a hidden wonder",
      additionalComments: `COSTUME: Classic explorer outfit with khaki safari shirt with rolled sleeves, matching shorts, sturdy hiking boots with thick soles, wide-brimmed explorer hat, binoculars around neck, small backpack with supplies. Has field notebook and pencil for sketching discoveries.
FOR MULTIPLE CHARACTERS: Same explorer outfit, different accent colors on hat bands and backpacks - first with red accents, second with blue, third with green, fourth with orange, fifth with purple.

EXPLORER GEAR: Professional binoculars, field notebook for sketching animals, magnifying glass for examining insects, camera for photos, water canteen, compass for navigation.

EPIC SCENES: Setting out on jungle trail with backpack ready, spotting colorful parrot in canopy with binoculars, crossing rope bridge over rushing river, discovering hidden waterfall in clearing, gentle encounter with friendly jungle animal, sketching beautiful butterfly on flower, finding ancient ruins covered in vines, sunset view over jungle canopy, returning home with photos and sketches to share.

ENVIRONMENT: Lush green rainforest with towering trees, colorful exotic flowers, cascading waterfalls, rope bridges, ancient stone ruins, clearings with filtered sunlight, riverbanks with wildlife, tree canopy walkways.

TONE: Scientific curiosity and wonder. Respect for nature and wildlife. Gentle adventure without danger. Joy of discovery and learning about animals.`
    },
    hasHazardousEnvironments: true,
    hazardOutfitDescription: "safari explorer outfit with hat and binoculars"
  },

  {
    id: 'camping-adventure',
    category: 'adventure',
    titleSingular: "[[NAME]]'s Camping Adventure",
    titlePlural: "[[NAMES]]'s Camping Adventure",
    description: "Experience the great outdoors on an exciting camping trip!",
    thumbnailUrl: "/images/templates/camping.jpg",
    themeId: "nature",
    styleId: "modern3d",
    ageRange: { min: 3, max: 8 },
    defaultPageCount: 10,
    storyContext: {
      setting: "A beautiful forest campsite by a lake with mountains in background",
      plotOutline: "Goes camping, learns outdoor skills, and has a wonderful nature adventure",
      additionalComments: `COSTUME: Cozy camping outfit with plaid flannel shirt in red and black, comfortable jeans, sturdy hiking boots, warm vest for evening, baseball cap during day. Has flashlight and s'mores stick at campfire. Sleeping bag and lantern for night scenes.
FOR MULTIPLE CHARACTERS: Same camping style, different flannel colors - first in red plaid, second in blue plaid, third in green plaid, fourth in orange plaid, fifth in purple plaid.

CAMPING GEAR: Cheerful tent with warm glow inside, campfire with crackling flames, fishing rod by lake, hiking backpack, compass and map, marshmallow roasting sticks, cozy sleeping bags, camping lantern.

EPIC SCENES: Arriving at campsite with tent and gear, setting up tent with determined expression, hiking scenic trail through forest, discovering animal tracks in mud, fishing at peaceful lake at golden hour, roasting marshmallows over campfire with happy expression, stargazing lying on blanket with wonder, waking up in tent to sunrise, packing up with happy memories to take home.

ENVIRONMENT: Forested campsite with tall pines, calm lake reflecting mountains, hiking trails through wildflowers, campfire circle with log seats, starry night sky with visible constellations, misty morning in forest, scenic overlook points.

TONE: Wholesome outdoor fun. Gentle adventure and discovery. Family bonding atmosphere. Appreciation for nature's beauty. Cozy evening campfire warmth.`
    }
  },

  {
    id: 'world-traveler',
    category: 'adventure',
    titleSingular: "[[NAME]] the World Traveler",
    titlePlural: "[[NAMES]] the World Travelers",
    description: "Journey around the globe visiting amazing destinations!",
    thumbnailUrl: "/images/templates/world-traveler.jpg",
    themeId: "adventure",
    styleId: "modern3d",
    ageRange: { min: 4, max: 10 },
    defaultPageCount: 12,
    storyContext: {
      setting: "Famous landmarks and destinations around the world",
      plotOutline: "Embarks on world tour, visits famous landmarks, and learns about different cultures",
      additionalComments: `COSTUME: Stylish traveler outfit with comfortable travel jacket with many pockets, passport holder on lanyard, rolling suitcase with stickers from visited countries, camera around neck, comfortable walking shoes. Adapts local accessories at each destination (beret in Paris, etc.).
FOR MULTIPLE CHARACTERS: Same traveler style, different jacket colors - first in navy, second in burgundy, third in olive green, fourth in tan, fifth in charcoal. Each has matching suitcase.

TRAVEL GEAR: Passport with stamps collecting, world map with pins for visited places, travel journal for memories, camera for photos, phrase book for languages, souvenir collection growing.

EPIC SCENES: Boarding airplane with excited wave, posing at Eiffel Tower in Paris, riding gondola in Venice canals, standing at Great Wall of China, watching sunset at Taj Mahal, safari jeep in African savanna, snorkeling at Great Barrier Reef, eating local food with delighted expression, returning home with suitcase covered in stickers.

ENVIRONMENT: International airport departures, Paris with Eiffel Tower, Venice canals with gondolas, Chinese landmarks with pagodas, Indian palaces with intricate details, African savanna with wildlife, Australian reef with tropical fish, home with souvenirs displayed.

TONE: Excitement of travel and discovery. Respect for different cultures. Wonder at world's beauty. Joy of collecting experiences and memories.`
    }
  },

  // ============================================
  // SPORTS TEMPLATES
  // ============================================
  {
    id: 'soccer-adventure',
    category: 'sports',
    titleSingular: "[[NAME]]'s Soccer Adventure",
    titlePlural: "[[NAMES]]'s Soccer Adventure",
    description: "Score the winning goal and become a soccer star!",
    thumbnailUrl: "/images/templates/soccer.jpg",
    themeId: "sports",
    styleId: "modern3d",
    ageRange: { min: 3, max: 10 },
    defaultPageCount: 10,
    storyContext: {
      setting: "A professional soccer stadium with cheering crowds and pristine green field",
      plotOutline: "Trains hard, joins the big game, and scores the championship-winning goal",
      additionalComments: `COSTUME: Bright red team jersey with bold number 10 on back, matching red shorts, tall white soccer socks with red stripes, black cleats with red accents. Hair styled back from face. Shin guards visible under socks.
FOR MULTIPLE CHARACTERS: Same uniform style, different jersey numbers and colors - first in red (#10), second in blue (#7), third in gold (#9), fourth in green (#3), fifth in white (#1).

VISUAL SKILL: Dynamic soccer technique - powerful kicks with motion blur on ball, headers with determined expression, quick dribbling with ball at feet, diving saves. Show grass and dirt flying from cleats during sharp pivots and slides.

EPIC SCENES: Arriving at stadium with crowd visible in stands, warm-up stretches on lush green field, intense dribbling past opponents, dramatic slow-motion shot on goal with goalkeeper diving, ball hitting back of net with ripple effect, celebration with arms raised and huge smile, holding golden trophy with confetti falling, medal ceremony with proud expression.

ENVIRONMENT: Professional soccer stadium with bright green grass, crisp white line markings, packed stands with colorful crowd, blue sky with dramatic clouds, bright stadium lights glowing for evening games.

TONE: Determination during play, pure joy in celebration. Athletic movement and triumphant victory moments. Pride in achievement.`
    },
    conflictType: 'competition',
    antagonist: {
      type: 'rival',
      name: 'The Dragons',
      description: 'the defending champion team known for their unbeatable record',
      threat: 'winning the championship trophy and keeping their winning streak'
    }
  },

  {
    id: 'basketball-star',
    category: 'sports',
    titleSingular: "[[NAME]] the Basketball Star",
    titlePlural: "[[NAMES]] the Basketball Stars",
    description: "Shoot hoops and become a basketball champion!",
    thumbnailUrl: "/images/templates/basketball.jpg",
    themeId: "sports",
    styleId: "modern3d",
    ageRange: { min: 3, max: 10 },
    defaultPageCount: 10,
    storyContext: {
      setting: "A gleaming basketball arena with polished courts and roaring fans",
      plotOutline: "Practices skills, plays in the championship game, and makes the winning shot",
      additionalComments: `COSTUME: Professional basketball jersey in vibrant orange with white trim, number 23 on chest and back, matching orange shorts with white side stripes, high-top basketball shoes in orange and white, wristband on one arm. Hair kept out of face with headband.
FOR MULTIPLE CHARACTERS: Same uniform style, different numbers and colors - first in orange (#23), second in purple (#7), third in green (#11), fourth in red (#5), fifth in blue (#32).

VISUAL SKILL: Athletic basketball moves - high jumping with extended arm for slam dunk, perfect shooting form with ball at fingertips, quick crossover dribbling low to ground, no-look passes. Show motion blur and sweat droplets during intense action.

EPIC SCENES: Walking into arena with spotlights overhead, practice shots swishing through net, intense game with crowd on feet, driving to basket past defenders, leaping high for slam dunk with ball above rim, perfect three-pointer at buzzer with ball mid-arc, net swishing as ball goes through, team celebration with jumping and cheering, holding championship trophy overhead.

ENVIRONMENT: Professional basketball arena with gleaming hardwood floor, arena lights creating dramatic atmosphere, packed stands with fans in team colors, scoreboard showing close game, confetti falling during celebration.

TONE: Competitive intensity and athletic grace. Team spirit and determination. Explosive celebration at victory. Pride and accomplishment.`
    },
    conflictType: 'competition',
    antagonist: {
      type: 'rival',
      name: 'The Thunder',
      description: 'last year\'s champions with their star player',
      threat: 'winning the championship and bragging rights'
    }
  },

  {
    id: 'karate-journey',
    category: 'sports',
    titleSingular: "[[NAME]] the Karate Champion",
    titlePlural: "[[NAMES]] the Karate Champions",
    description: "Train with a wise sensei and earn the champion belt!",
    thumbnailUrl: "/images/templates/karate.jpg",
    themeId: "sports",
    styleId: "modern3d",
    ageRange: { min: 3, max: 10 },
    defaultPageCount: 10,
    storyContext: {
      setting: "A traditional Japanese dojo with polished wooden floors and ancient scrolls",
      plotOutline: "Begins training, learns discipline and technique, and earns a new belt rank",
      additionalComments: `COSTUME: Traditional white karate gi (uniform) with crisp folds, belt tied properly at waist showing rank (progresses through story: white → yellow → orange → red), bare feet on training mat. Hair neatly styled back from face. Focused, determined expression.
FOR MULTIPLE CHARACTERS: Same white gi, different final belt colors earned - first earns red, second earns blue, third earns gold, fourth earns purple, fifth earns green.

VISUAL SKILL: Powerful karate technique - strong punching stance with extended fist, high kicks with pointed toes, defensive blocks with forearms raised, kata forms with flowing movement, board-breaking with dramatic splinter effect. Motion lines emphasize speed and power.

EPIC SCENES: Entering traditional dojo with respectful bow, meeting wise sensei with respectful greeting, learning basic stance with intense focus, practicing punches on training pads with visible impact, perfecting high kick with leg fully extended, meditation moment with eyes closed, sparring practice with protective gear, dramatic board break with wood splintering, belt ceremony with sensei presenting new colored belt, final triumphant pose.

ENVIRONMENT: Traditional Japanese dojo with polished wooden floors, wall of mirrors reflecting movement, training equipment (pads, boards, bags), Japanese calligraphy scrolls on walls, natural light through paper screens, trophy display case.

TONE: Discipline, respect, personal growth. Progression from uncertain beginner to confident martial artist. Respectful bowing, focused training, triumphant achievement.`
    }
  },

  {
    id: 'swimming-adventure',
    category: 'sports',
    titleSingular: "[[NAME]]'s Swimming Adventure",
    titlePlural: "[[NAMES]]'s Swimming Adventure",
    description: "Dive in and become a swimming champion!",
    thumbnailUrl: "/images/templates/swimming.jpg",
    themeId: "sports",
    styleId: "modern3d",
    ageRange: { min: 3, max: 10 },
    defaultPageCount: 10,
    storyContext: {
      setting: "An Olympic-style swimming pool with crystal clear lanes and diving platforms",
      plotOutline: "Overcomes fear of water, trains with a coach, and wins the swimming race",
      additionalComments: `COSTUME: Sleek racing swimsuit in bright red with white racing stripes down sides, fitted swim cap in matching red, professional swim goggles with tinted lenses pushed up on forehead or over eyes. Lean, athletic posture.
FOR MULTIPLE CHARACTERS: Same swimsuit style, different colors - first in red, second in blue, third in gold, fourth in green, fifth in purple. Matching caps and goggle straps.

VISUAL SKILL: Powerful swimming technique - streamlined dive entry with minimal splash, freestyle stroke with arm reaching forward, butterfly stroke with dramatic water spray, flip turn at wall with tucked rotation. Water splashes and droplets catch light dramatically.

EPIC SCENES: Standing nervously at pool edge for first time, kind coach giving encouragement, learning to float with peaceful expression, practicing strokes with improving form, standing on starting blocks ready to race, perfect dive entry in racing position, powerful swimming with water spraying, touching wall first with victorious expression, standing on medal podium with gold medal, holding trophy with wet hair and huge smile.

ENVIRONMENT: Olympic-style pool with crystal blue water, lane dividers in bright colors, starting blocks with numbers, timing scoreboard, spectator seating with cheering crowd, medal podium with flags, bright natatorium lighting.

TONE: Overcoming fear, building confidence. Smooth power in the water. Competitive excitement. Triumphant achievement and pride.`
    }
  },

  {
    id: 'dance-competition',
    category: 'sports',
    titleSingular: "[[NAME]]'s Dance Competition",
    titlePlural: "[[NAMES]]'s Dance Competition",
    description: "Shine on stage and win the dance competition!",
    thumbnailUrl: "/images/templates/dance.jpg",
    themeId: "performing-arts",
    styleId: "modern3d",
    ageRange: { min: 3, max: 10 },
    defaultPageCount: 10,
    storyContext: {
      setting: "A grand theater with spotlights and a gleaming stage",
      plotOutline: "Practices a special routine, performs on stage, and wins the competition",
      additionalComments: `COSTUME: Stunning performance outfit - sparkling leotard in royal purple covered in rhinestones that catch stage lights, flowing attached skirt that twirls beautifully, matching dance slippers with ribbons, hair styled elegantly with glitter accents. Sequins catch every light.
FOR MULTIPLE CHARACTERS: Same costume style, different colors - first in purple, second in turquoise, third in hot pink, fourth in gold, fifth in silver. Each sparkles uniquely.

VISUAL SKILL: Graceful dance technique - elegant arabesque with extended leg, spinning pirouette with skirt flowing, expressive arm movements, high leaps with pointed toes, dramatic poses with emotion. Motion blur shows spinning and flowing movement.

EPIC SCENES: Practicing in dance studio with mirror reflection, costume fitting with excited expression, arriving at grand theater with marquee, backstage preparation with nervous excitement, walking onto stage with spotlight, beginning routine with confident pose, mid-performance leap with perfect form, spinning with skirt creating color blur, final pose with arms extended, receiving trophy and flowers with radiant smile.

ENVIRONMENT: Dance studio with mirrors and barre, grand theater with red velvet curtains, polished stage with gleaming floor, dramatic spotlights creating beams, audience in darkness beyond lights, backstage dressing room with costumes, awards stage with trophies.

TONE: Artistic expression and grace. Pre-performance butterflies becoming confidence. Joy of movement and music. Triumphant standing ovation.`
    }
  },

  {
    id: 'championship-game',
    category: 'sports',
    titleSingular: "[[NAME]]'s Championship Game",
    titlePlural: "[[NAMES]]'s Championship Game",
    description: "Lead the team to victory in the big championship!",
    thumbnailUrl: "/images/templates/championship.jpg",
    themeId: "sports",
    styleId: "modern3d",
    ageRange: { min: 4, max: 10 },
    defaultPageCount: 10,
    storyContext: {
      setting: "A packed sports stadium during the championship finals",
      plotOutline: "Overcomes challenges, plays the championship game, and achieves victory",
      additionalComments: `COSTUME: Team uniform with home jersey in bold blue with gold trim, number 1 prominently displayed, matching shorts with gold stripe, athletic socks and cleats. Captain's armband on upper arm. Athletic tape on wrists for intensity.
FOR MULTIPLE CHARACTERS: Same uniform style, different numbers - first wears #1, second wears #5, third wears #8, fourth wears #12, fifth wears #21. All in matching blue and gold.

VISUAL SKILL: Athletic sports action - determined running with athletic stride, powerful plays with focused expression, celebration moves with fist pumps and jumps. Grass stains and sweat show hard-fought effort.

EPIC SCENES: Team huddle with hands in center, entering stadium through tunnel with crowd roaring, game action with intense focus, crucial play executed perfectly, clock showing final seconds, winning moment captured with ball in hand, jumping celebration with arms raised, confetti falling all around, championship trophy raised overhead, medal ceremony with proud smile.

ENVIRONMENT: Packed championship stadium with roaring crowd, pristine playing field under bright lights, giant scoreboard showing close score, team benches with coaches, trophy podium for ceremony, locker room celebration scene.

TONE: Team determination and unity. Pressure of big moment. Explosive joy at victory. Pride in hard-earned achievement.`
    }
  },

  // ============================================
  // SCIENCE/SPACE TEMPLATES
  // ============================================
  {
    id: 'space-mission',
    category: 'science',
    titleSingular: "[[NAME]]'s Space Mission",
    titlePlural: "[[NAMES]]'s Space Mission",
    description: "Blast off into space and explore the cosmos!",
    thumbnailUrl: "/images/templates/space.jpg",
    themeId: "space",
    styleId: "modern3d",
    ageRange: { min: 3, max: 10 },
    defaultPageCount: 12,
    storyContext: {
      setting: "A space station orbiting Earth and the vast reaches of outer space",
      plotOutline: "Trains as an astronaut, launches into space, and completes an important mission",
      additionalComments: `COSTUME: Professional astronaut spacesuit in bright white with bold NASA-style patches, American flag on shoulder, name embroidered on chest, gold-tinted helmet visor (can show reflection of space), life support backpack with tubes and gauges, thick gloves with grip pads, magnetic boots for space walks. Orange launch suit for rocket scenes.
FOR MULTIPLE CHARACTERS: Same spacesuit style, different accent stripe colors - first with red stripes, second with blue stripes, third with gold stripes, fourth with green stripes, fifth with purple stripes. Each has unique name patch.

SPACE EQUIPMENT: High-tech spacecraft with detailed control panels, glowing instrument displays, astronaut seat harnesses, mission patches on walls. Handheld scanner for collecting data, tether cables for spacewalks.

EPIC SCENES: Training in mission control with simulators, suiting up with helmet in hands, walking to launch pad with rocket towering behind, dramatic rocket launch with fire and smoke, floating in zero gravity with amazed expression, spacewalk outside station with Earth in background, collecting samples from asteroid or moon surface, viewing Earth through station window, splashdown return with parachutes deployed, hero's welcome parade.

ENVIRONMENT: Mission control with giant screens, launch pad with massive rocket, spacecraft interior with glowing controls, space station modules with windows, Earth's blue curve visible from orbit, asteroid field or lunar surface, distant stars and nebulae.

TONE: Scientific wonder and discovery. Bravery facing the unknown. Awe at space's beauty. Pride in successful mission completion.`
    },
    hasHazardousEnvironments: true,
    hazardOutfitDescription: "astronaut spacesuit with helmet and life support backpack",
    conflictType: 'environmental',
    antagonist: {
      type: 'force_of_nature',
      description: 'the dangers of space - asteroid fields, solar flares, and equipment malfunctions',
      threat: 'completing the mission safely despite the hostile void of space'
    }
  },

  {
    id: 'lunar-escape',
    category: 'science',
    titleSingular: "[[NAME]]'s Lunar Adventure",
    titlePlural: "[[NAMES]]'s Lunar Adventure",
    description: "Explore the moon and discover its secrets!",
    thumbnailUrl: "/images/templates/lunar.jpg",
    themeId: "space",
    styleId: "modern3d",
    ageRange: { min: 4, max: 10 },
    defaultPageCount: 12,
    storyContext: {
      setting: "The moon's surface with craters, a lunar base, and Earth visible in the black sky",
      plotOutline: "Lands on the moon, explores the surface, and makes an amazing discovery",
      additionalComments: `COSTUME: Advanced lunar exploration suit in sleek silver with glowing blue accents on joints and helmet, reinforced for moon conditions, large fishbowl helmet with heads-up display graphics visible inside, heavy-duty boots for moon walking, utility belt with scientific tools. Suit has lunar mission patches.
FOR MULTIPLE CHARACTERS: Same suit style, different accent colors - first with blue glowing accents, second with orange, third with green, fourth with purple, fifth with red. Each has unique mission patch.

LUNAR EQUIPMENT: Lunar rover vehicle with big wheels, sample collection containers, scientific scanner with glowing readouts, flag for planting, camera for documenting, communication antenna on backpack.

EPIC SCENES: Lunar lander touching down with dust kicking up, first step onto moon surface (bootprint in lunar dust), bouncing in low gravity with joyful expression, driving lunar rover across crater rim, planting flag in dramatic pose, discovering unusual crystal formation glowing in crater, collecting samples with careful focus, looking up at Earth hanging in black sky, return launch from moon surface, hero return to Earth with discovery.

ENVIRONMENT: Gray lunar surface with craters and boulders, Earth rising over horizon (Earthrise), black sky filled with stars, lunar base dome with lights, crater with mysterious glowing crystals, lunar dust clouds from movement, landing site with lander and flag.

TONE: Pioneer spirit and exploration. Scientific discovery excitement. Wonder at cosmic perspectives. Pride in achievement.`
    },
    hasHazardousEnvironments: true,
    hazardOutfitDescription: "lunar exploration suit with helmet and utility belt",
    conflictType: 'discovery',
    antagonist: {
      type: 'challenge',
      description: 'the harsh lunar environment and a mysterious signal from beneath the surface',
      threat: 'unraveling the moon\'s secrets before the mission window closes'
    }
  },

  {
    id: 'young-scientist',
    category: 'science',
    titleSingular: "[[NAME]] the Young Scientist",
    titlePlural: "[[NAMES]] the Young Scientists",
    description: "Discover amazing things through science experiments!",
    thumbnailUrl: "/images/templates/scientist.jpg",
    themeId: "science",
    styleId: "modern3d",
    ageRange: { min: 4, max: 10 },
    defaultPageCount: 10,
    storyContext: {
      setting: "A colorful science lab and science fair exhibition hall",
      plotOutline: "Develops a science project, conducts experiments, and wins the science fair",
      additionalComments: `COSTUME: Professional kid scientist look with crisp white lab coat that's perfectly sized, safety goggles pushed up on forehead (down over eyes during experiments), colorful t-shirt with science graphic visible under coat, clipboard with notes, pen in coat pocket. Hair neat and out of face.
FOR MULTIPLE CHARACTERS: Same lab coat style, different t-shirt colors visible - first in blue, second in green, third in purple, fourth in orange, fifth in red. Each has unique experiment specialty.

SCIENCE EQUIPMENT: Bubbling beakers with colorful liquids, microscope with sample slides, safety equipment properly worn, experiment notebook with diagrams, laptop showing data, robot or volcano project on display table.

EPIC SCENES: Getting inspired by wondering "why?" about something, sketching experiment idea in notebook, setting up equipment in lab carefully, mixing chemicals with color-changing reaction, looking through microscope with amazed expression, building project with focused determination, testing hypothesis with excited anticipation, project display at science fair, explaining discovery to impressed judges, holding first place trophy and blue ribbon.

ENVIRONMENT: Colorful school science lab with equipment, home garage workshop with project in progress, science fair exhibition hall with display tables, award ceremony stage, encouraging mentor or teacher present.

TONE: Curiosity driving discovery. Careful scientific method. Excitement when experiment works. Pride in sharing knowledge with others.`
    }
  },

  {
    id: 'dinosaur-discovery',
    category: 'science',
    titleSingular: "[[NAME]]'s Dinosaur Discovery",
    titlePlural: "[[NAMES]]'s Dinosaur Discovery",
    description: "Travel back in time to meet real dinosaurs!",
    thumbnailUrl: "/images/templates/dinosaur.jpg",
    themeId: "adventure",
    styleId: "modern3d",
    ageRange: { min: 3, max: 8 },
    defaultPageCount: 10,
    storyContext: {
      setting: "Prehistoric Earth during the time of the dinosaurs",
      plotOutline: "Travels to dinosaur times, meets amazing prehistoric creatures, and returns safely home",
      additionalComments: `COSTUME: Young paleontologist explorer outfit with khaki vest covered in pockets, safari hat to protect from prehistoric sun, sturdy hiking boots, field notebook for sketching dinosaurs, camera around neck. Binoculars for spotting distant dinos.
FOR MULTIPLE CHARACTERS: Same explorer outfit, different vest colors - first in khaki, second in olive green, third in sandy brown, fourth in rust red, fifth in charcoal. Each has unique field specialty.

DINOSAUR FRIENDS: Meeting friendly herbivore dinosaurs - gentle long-necked Brachiosaurus, plated Stegosaurus, horned Triceratops. Dinosaurs shown as majestic and awe-inspiring, not scary. Baby dinosaurs are extra friendly.

EPIC SCENES: Discovering time portal with swirling lights, stepping through into prehistoric jungle, first amazed view of towering dinosaur, sketching dinosaur in field notebook, gentle encounter with baby dinosaur, riding on friendly Brachiosaurus neck, hiding behind fern from passing T-Rex (exciting but safe), watching Pterodactyls soar overhead, saying goodbye to dinosaur friend, returning through portal with notebook full of drawings.

ENVIRONMENT: Lush prehistoric jungle with giant ferns and ancient trees, volcanic mountains in distance, clear blue prehistoric sky, watering hole where dinosaurs gather, rocky terrain with visible fossils, nest with dinosaur eggs.

TONE: Wonder at seeing real dinosaurs. Scientific curiosity about prehistoric life. Gentle encounters with herbivores. Exciting but not scary atmosphere. Bittersweet goodbye when leaving.`
    },
    hasHazardousEnvironments: true,
    hazardOutfitDescription: "paleontologist explorer outfit with safari hat and vest"
  },

  {
    id: 'robot-inventor',
    category: 'science',
    titleSingular: "[[NAME]] the Robot Inventor",
    titlePlural: "[[NAMES]] the Robot Inventors",
    description: "Build an amazing robot friend and go on adventures!",
    thumbnailUrl: "/images/templates/robot.jpg",
    themeId: "science",
    styleId: "modern3d",
    ageRange: { min: 4, max: 10 },
    defaultPageCount: 10,
    storyContext: {
      setting: "A creative workshop where inventions come to life",
      plotOutline: "Designs and builds a robot, brings it to life, and goes on an adventure together",
      additionalComments: `COSTUME: Young inventor look with colorful work overalls over graphic tee, safety goggles on head, work gloves in back pocket, tool belt with wrenches and screwdrivers, utility boots. Small grease smudge on cheek shows hard work.
FOR MULTIPLE CHARACTERS: Same inventor style, different overall colors - first in blue, second in red, third in yellow, fourth in green, fifth in orange. Each has unique robot specialty.

ROBOT COMPANION: Friendly robot with expressive LED face that shows emotions, rounded friendly design (not scary), articulated arms and legs, antenna on head, glowing chest power core, wheels or legs for movement. Robot beeps and whirs expressively.

EPIC SCENES: Sketching robot design on drafting paper, workshop cluttered with parts and tools, carefully assembling robot piece by piece, connecting final wire with hopeful expression, pressing power button with anticipation, robot's eyes lighting up for first time, robot's first wobbly steps, high-five between inventor and robot, robot helping with a task proudly, inventor and robot posing for photo together, entering robot in competition, winning together.

ENVIRONMENT: Creative workshop with workbench and tools, bins of robot parts and circuit boards, blueprints on walls, testing area for robot, robot competition arena with judges, home setting for daily adventures.

TONE: Creative invention and problem-solving. Wonder when robot comes to life. Developing friendship with creation. Pride in building something amazing.`
    }
  },

  // ============================================
  // OCEAN/NATURE TEMPLATES
  // ============================================
  {
    id: 'ocean-friends',
    category: 'adventure',
    titleSingular: "[[NAME]]'s Ocean Friends",
    titlePlural: "[[NAMES]]'s Ocean Friends",
    description: "Dive underwater and make friends with sea creatures!",
    thumbnailUrl: "/images/templates/ocean.jpg",
    themeId: "ocean",
    styleId: "modern3d",
    ageRange: { min: 3, max: 8 },
    defaultPageCount: 10,
    storyContext: {
      setting: "A magical underwater world where you can breathe and talk to sea creatures",
      plotOutline: "Receives magical ability to breathe underwater, befriends sea creatures, and helps save the reef",
      additionalComments: `COSTUME: Magical underwater explorer outfit with shimmering wetsuit in aqua blue with scale patterns, enchanted breathing necklace that glows with bubbles, swim fins in matching blue, waterproof pouch for treasures. Hair flows freely in water. Scale patterns catch underwater light beautifully.
FOR MULTIPLE CHARACTERS: Same wetsuit style, different colors - first in aqua blue, second in coral pink, third in sea green, fourth in golden yellow, fifth in violet. Each necklace glows differently.

SEA CREATURE FRIENDS: Friendly dolphin companion with playful personality, wise old sea turtle, colorful tropical fish school, gentle manatee, shy octopus that waves. All creatures are friendly and expressive with big eyes.

EPIC SCENES: Discovering magical necklace on beach, first breath underwater with amazed expression, swimming gracefully through kelp forest, meeting friendly dolphin with joyful spin, playing with school of colorful fish, exploring coral reef with wonder, discovering treasure chest in sand, helping tangled sea turtle with careful hands, riding on dolphin's back through ocean, returning to surface with magical memories.

ENVIRONMENT: Crystal-clear tropical ocean, vibrant coral reef with colorful corals, kelp forest swaying in current, sandy ocean floor with shells, underwater cave with mysterious glow, shipwreck to explore, surface sunshine streaming through water.

TONE: Magical underwater wonder. Gentle friendship with sea creatures. Helping ocean friends in need. Joy of underwater exploration.`
    },
    hasHazardousEnvironments: true,
    hazardOutfitDescription: "magical wetsuit with enchanted breathing necklace",
    conflictType: 'environmental',
    antagonist: {
      type: 'force_of_nature',
      description: 'pollution threatening the coral reef and sea creature friends',
      threat: 'saving the reef before it\'s too late'
    }
  },

  {
    id: 'submarine-adventure',
    category: 'adventure',
    titleSingular: "[[NAME]]'s Submarine Adventure",
    titlePlural: "[[NAMES]]'s Submarine Adventure",
    description: "Pilot a submarine to explore the ocean depths!",
    thumbnailUrl: "/images/templates/submarine.jpg",
    themeId: "ocean",
    styleId: "modern3d",
    ageRange: { min: 4, max: 10 },
    defaultPageCount: 12,
    storyContext: {
      setting: "A state-of-the-art submarine exploring the mysterious ocean depths",
      plotOutline: "Takes command of a submarine, explores the deep ocean, and makes an incredible discovery",
      additionalComments: `COSTUME: Submarine captain uniform with navy blue captain's jacket, gold trim and buttons, captain's cap with anchor insignia, khaki pants, polished boots. Badge of rank on collar. Confident captain's posture.
FOR MULTIPLE CHARACTERS: Same uniform style, different rank insignias - first as Captain, second as First Mate, third as Navigator, fourth as Engineer, fifth as Science Officer. Each has matching hat style.

SUBMARINE: Sleek yellow research submarine with large observation windows, control panels with glowing buttons, periscope station, robotic arms for collecting samples, powerful headlights for deep ocean. Named and numbered on hull.

EPIC SCENES: Boarding submarine at dock, taking position at captain's chair, descending into deep blue water, passing through schools of fish, exploring coral reef through window, discovering glowing deep-sea creatures, finding sunken ship with treasure, collecting sample with robot arm, surfacing to triumphant sunset, displaying discovery to amazed scientists.

ENVIRONMENT: Submarine dock at research station, ocean descent through color zones (light to dark), coral reef with biodiversity, deep ocean with bioluminescent creatures, ocean floor with geographic features, sunken ship interior, research station harbor.

TONE: Scientific exploration and command. Wonder at deep ocean mysteries. Captain's responsibility and leadership. Pride in important discovery.`
    },
    hasHazardousEnvironments: true,
    hazardOutfitDescription: "submarine captain uniform with cap and insignia",
    conflictType: 'discovery',
    antagonist: {
      type: 'force_of_nature',
      description: 'the crushing depths and mysterious creatures of the deep ocean',
      threat: 'exploring the unknown abyss and making a groundbreaking discovery'
    }
  },

  // ============================================
  // MYSTERY/DETECTIVE TEMPLATES
  // ============================================
  {
    id: 'detective-case',
    category: 'mystery',
    titleSingular: "[[NAME]] the Detective",
    titlePlural: "[[NAMES]] the Detectives",
    description: "Solve the mystery and crack the case!",
    thumbnailUrl: "/images/templates/detective.jpg",
    themeId: "mystery",
    styleId: "modern3d",
    ageRange: { min: 4, max: 10 },
    defaultPageCount: 10,
    storyContext: {
      setting: "A mysterious neighborhood with clues hidden everywhere",
      plotOutline: "Receives a puzzling case, gathers clues, and solves the mystery",
      additionalComments: `COSTUME: Classic young detective outfit with tan trench coat, deerstalker detective cap, magnifying glass always in hand or pocket, small notebook and pencil, keen observant expression. Coat collar can be turned up for serious investigating.
FOR MULTIPLE CHARACTERS: Same detective style, different trench coat colors - first in tan, second in gray, third in navy blue, fourth in brown, fifth in black. Each has unique detective tool specialty.

DETECTIVE GEAR: Large magnifying glass with brass handle, clue notebook with scribbled notes, fingerprint kit, camera for evidence photos, detective badge (self-made), map of neighborhood with pins.

EPIC SCENES: Receiving mysterious case assignment, examining first clue with magnifying glass, taking notes in detective notebook, following footprints to new location, questioning friendly witness, piecing together clues on cork board, "aha!" moment when solution clicks, confronting (friendly) culprit with evidence, revealing solution to amazed audience, receiving thanks and reward for solving case.

ENVIRONMENT: Detective office/bedroom with case board and string, neighborhood streets to investigate, scenes of friendly "crime" (missing cookies, etc.), library for research, hidden spots with clues to discover.

TONE: Clever observation and deduction. Building mystery and suspense. Satisfying solution reveal. Pride in solving the puzzle.`
    },
    conflictType: 'mystery',
    antagonist: {
      type: 'challenge',
      description: 'a puzzling case with hidden clues scattered throughout the neighborhood',
      threat: 'solving the mystery before the trail goes cold'
    }
  },

  {
    id: 'treasure-mystery',
    category: 'mystery',
    titleSingular: "[[NAME]]'s Treasure Mystery",
    titlePlural: "[[NAMES]]'s Treasure Mystery",
    description: "Follow the clues to discover hidden treasure!",
    thumbnailUrl: "/images/templates/treasure.jpg",
    themeId: "mystery",
    styleId: "modern3d",
    ageRange: { min: 4, max: 10 },
    defaultPageCount: 10,
    storyContext: {
      setting: "A historic area with hidden passages and buried secrets",
      plotOutline: "Finds an old treasure map, solves riddles, and discovers amazing treasure",
      additionalComments: `COSTUME: Treasure hunter explorer outfit with adventure jacket in brown leather, comfortable cargo pants with pockets for finds, sturdy boots for climbing, headlamp for dark passages, rope coiled at belt. Weathered treasure map always visible.
FOR MULTIPLE CHARACTERS: Same explorer style, different jacket colors - first in brown, second in olive, third in burgundy, fourth in tan, fifth in black. Each carries different tools.

TREASURE HUNTING GEAR: Aged treasure map with torn edges, compass for navigation, headlamp for caves, small shovel for digging, brush for uncovering finds, satchel for collecting treasure.

EPIC SCENES: Discovering old map in attic trunk, deciphering first riddle with concentrated expression, following map through woods, discovering hidden entrance behind waterfall, navigating dark passage with headlamp beam, solving puzzle lock with ancient symbols, narrow passage opening to treasure chamber, golden glow of treasure illuminating face, opening chest to sparkling treasure, displaying discovery with proud smile.

ENVIRONMENT: Dusty attic where map is found, forest path following map route, waterfall hiding cave entrance, underground tunnels with ancient markings, puzzle room with mechanisms, final treasure chamber with golden glow, return home with treasures displayed.

TONE: Mysterious discovery and puzzle-solving. Excitement of following clues. Tension in dark passages. Triumphant treasure reveal.`
    },
    conflictType: 'mystery',
    antagonist: {
      type: 'challenge',
      description: 'ancient riddles and booby-trapped passages protecting the treasure',
      threat: 'deciphering the clues before someone else finds the treasure first'
    }
  },

  {
    id: 'missing-pet',
    category: 'mystery',
    titleSingular: "[[NAME]] Finds the Missing Pet",
    titlePlural: "[[NAMES]] Find the Missing Pet",
    description: "Use detective skills to find a lost pet friend!",
    thumbnailUrl: "/images/templates/missing-pet.jpg",
    themeId: "mystery",
    styleId: "modern3d",
    ageRange: { min: 3, max: 8 },
    defaultPageCount: 10,
    storyContext: {
      setting: "A friendly neighborhood where a beloved pet has gone missing",
      plotOutline: "Learns that a pet is missing, searches the neighborhood, and happily reunites pet with owner",
      additionalComments: `COSTUME: Neighborhood detective casual look with comfortable clothes, sneakers for running around, binoculars around neck, small backpack with search supplies, "Lost Pet" flyer in hand. Determined, helpful expression.
FOR MULTIPLE CHARACTERS: Same casual style, different shirt colors - first in blue, second in yellow, third in green, fourth in orange, fifth in purple. Each carries different search supplies.

SEARCH EQUIPMENT: "Lost Pet" flyers with photo, binoculars for scanning areas, treats to lure pet out, pet carrier ready for rescue, map of neighborhood with search areas marked, flashlight for dark spots.

EPIC SCENES: Seeing sad neighbor holding missing pet flyer, determined decision to help, creating search plan on neighborhood map, checking under porches and bushes, asking helpful neighbors for tips, spotting clue (paw prints, fur tuft), following trail with binoculars, finding scared pet hiding in cozy spot, gentle approach with treats, joyful reunion with crying-happy owner, receiving heartfelt thanks.

ENVIRONMENT: Friendly neighborhood with houses and yards, parks and playgrounds to search, bushes and hiding spots, kind neighbor's doors to knock on, final hiding spot (garden shed, under porch, tree hollow).

TONE: Compassion for worried pet owner. Determination to help. Careful searching and clue-following. Heartwarming joyful reunion.`
    }
  },

  // ============================================
  // ANIMALS TEMPLATES
  // ============================================
  {
    id: 'puppy-pal',
    category: 'animals',
    titleSingular: "[[NAME]]'s Puppy Pal",
    titlePlural: "[[NAMES]]'s Puppy Pal",
    description: "Get a new puppy friend and learn about pet care!",
    thumbnailUrl: "/images/templates/puppy.jpg",
    themeId: "animals",
    styleId: "modern3d",
    ageRange: { min: 3, max: 7 },
    defaultPageCount: 10,
    storyContext: {
      setting: "Home and neighborhood with a new puppy companion",
      plotOutline: "Gets a new puppy, learns to care for it, and develops a loving friendship",
      additionalComments: `COSTUME: Cozy casual outfit with comfortable sweater or t-shirt, jeans, sneakers good for running with puppy. Has puppy leash in hand, treat pouch at waist, bandana that matches puppy's collar.
FOR MULTIPLE CHARACTERS: Same casual style, different sweater colors - first in blue, second in red, third in yellow, fourth in green, fifth in purple. Each has unique way of playing with puppy.

PUPPY COMPANION: Adorable fluffy puppy with big expressive eyes, floppy ears, waggy tail, soft golden or brown fur. Puppy has red collar with name tag. Shows various expressions - excited, sleepy, playful, curious.

EPIC SCENES: First meeting with puppy with love-at-first-sight expression, bringing puppy home with careful carry, setting up cozy puppy bed, learning to fill food and water bowls, first walk on leash with puppy pulling excitedly, playing fetch in sunny backyard, giving puppy first bath with bubbles everywhere, teaching puppy to sit with proud moment when it works, snuggling together on couch for nap, puppy running to greet with tail wagging.

ENVIRONMENT: Welcoming home with puppy supplies ready, sunny backyard for playing, neighborhood sidewalks for walks, pet store for supplies, veterinarian office for checkup, cozy living room for snuggles.

TONE: Unconditional love between child and pet. Learning responsibility through care. Joy in playful moments. Cozy comfort in quiet times.`
    }
  },

  {
    id: 'zoo-adventure',
    category: 'animals',
    titleSingular: "[[NAME]]'s Zoo Adventure",
    titlePlural: "[[NAMES]]'s Zoo Adventure",
    description: "Explore the zoo and meet amazing animals from around the world!",
    thumbnailUrl: "/images/templates/zoo.jpg",
    themeId: "animals",
    styleId: "modern3d",
    ageRange: { min: 3, max: 7 },
    defaultPageCount: 10,
    storyContext: {
      setting: "A world-class zoo with animals from every continent",
      plotOutline: "Visits the zoo, learns about animals, and has a special behind-the-scenes experience",
      additionalComments: `COSTUME: Zoo visitor outfit with comfortable walking clothes, sun hat for protection, zoo map in hand, small backpack, camera for animal photos, comfortable walking shoes for all-day exploring.
FOR MULTIPLE CHARACTERS: Same visitor style, different hat colors - first with red hat, second with blue hat, third with yellow hat, fourth with green hat, fifth with purple hat.

ANIMAL FRIENDS: Variety of zoo animals - majestic lion with fluffy mane, tall giraffe with long neck, playful monkeys swinging, elegant elephant, colorful tropical birds, funny penguins waddling. All animals shown as magnificent and well-cared-for.

EPIC SCENES: Excited entry through zoo gates, lion roaring majestically through glass, giraffe bending down to eye level, elephant spraying water with trunk, monkeys making funny faces, feeding time with zookeeper assistance, penguin parade waddling past, special behind-scenes tour, baby animal encounter with gentle touch, zoo gift shop with stuffed animal souvenir, waving goodbye at exit with happy exhaustion.

ENVIRONMENT: Zoo entrance with exciting signage, themed habitat areas (African savanna, Arctic zone, rainforest), viewing areas with safe barriers, behind-scenes area with zookeeper, gift shop with animal merchandise, picnic area for lunch break.

TONE: Wonder at animal diversity. Learning through observation. Excitement at each new exhibit. Special memorable moments. Tired-happy end of day feeling.`
    }
  },

  {
    id: 'farm-fun',
    category: 'animals',
    titleSingular: "[[NAME]]'s Farm Fun",
    titlePlural: "[[NAMES]]'s Farm Fun",
    description: "Visit a farm and help with all the animals!",
    thumbnailUrl: "/images/templates/farm.jpg",
    themeId: "animals",
    styleId: "modern3d",
    ageRange: { min: 3, max: 7 },
    defaultPageCount: 10,
    storyContext: {
      setting: "A beautiful working farm with friendly animals and green pastures",
      plotOutline: "Visits a farm, helps with chores, and makes friends with all the farm animals",
      additionalComments: `COSTUME: Farm helper outfit with denim overalls, plaid shirt, rubber boots for mud, straw hat for sun, work gloves in pocket. Hay stuck in hair from playing in barn.
FOR MULTIPLE CHARACTERS: Same farm style, different plaid shirt colors - first in red plaid, second in blue plaid, third in green plaid, fourth in orange plaid, fifth in purple plaid.

FARM ANIMALS: Friendly farm animals - clucking hens with fluffy chicks, pink pig rolling in mud, woolly sheep with curly fleece, big red barn cow with bell, proud rooster crowing, loyal farm dog, fuzzy barn cat. All animals friendly and approachable.

EPIC SCENES: Arriving at farm gate with red barn visible, meeting friendly farmer with firm handshake, collecting eggs from hen house with careful hands, feeding baby goats with bottle, riding on tractor through fields, milking cow with farmer's help, playing with baby piglets in pen, petting woolly sheep, hayloft jump into soft hay pile, sunset picnic on checkered blanket, waving goodbye from farm gate.

ENVIRONMENT: Classic red barn with white trim, green pastures with wooden fences, chicken coop with roosting hens, pig pen with muddy puddles, sheep meadow with grazing flock, tractor in field, farmhouse with wraparound porch, hay bales stacked in barn.

TONE: Wholesome farm experience. Learning where food comes from. Gentle animal interactions. Hard work satisfaction. Country peace and beauty.`
    }
  },

  // ============================================
  // OCCASIONS/EDUCATIONAL TEMPLATES
  // ============================================
  {
    id: 'birthday-surprise',
    category: 'occasions',
    occasion: 'birthday',
    titleSingular: "[[NAME]]'s Birthday Surprise",
    titlePlural: "[[NAMES]]'s Birthday Surprise",
    description: "Have the most magical birthday celebration ever!",
    thumbnailUrl: "/images/templates/birthday.jpg",
    themeId: "celebration",
    styleId: "modern3d",
    ageRange: { min: 3, max: 8 },
    defaultPageCount: 10,
    storyContext: {
      setting: "A home decorated for an amazing birthday party",
      plotOutline: "Birthday morning, wonderful party celebration, and very special surprise ending",
      additionalComments: `COSTUME: Special birthday outfit - party clothes in favorite color, birthday crown or party hat with number on it, "Birthday Star" sash or badge. Special birthday smile throughout.
FOR MULTIPLE CHARACTERS: Same party style, different favorite colors - first in pink, second in blue, third in purple, fourth in green, fifth in orange. Each has matching party hat.

BIRTHDAY ELEMENTS: Colorful balloons in clusters, streamers and banners saying "Happy Birthday", wrapped presents with bows, birthday cake with candles matching age, party table with treats, confetti everywhere.

EPIC SCENES: Waking up excited on birthday morning, finding birthday balloons at bedroom door, opening cards from family with joy, decorating for party with streamers, greeting friends at door, party games with laughing friends, dramatic cake entrance with candles glowing, making birthday wish with eyes closed, blowing out candles with puffed cheeks, opening presents with surprised delight, hugging best present with huge smile.

ENVIRONMENT: Birthday-decorated home with balloons and streamers, party table with colorful spread, backyard with party games set up, living room with present pile, kitchen with spectacular cake being carried.

TONE: Special-day magic and excitement. Anticipation and joy. Feeling celebrated and loved. Gratitude for friends and family. Perfect birthday happiness.`
    }
  },

  {
    id: 'first-day-school',
    category: 'educational',
    occasion: 'first-day-of-school',
    titleSingular: "[[NAME]]'s First Day of School",
    titlePlural: "[[NAMES]]'s First Day of School",
    description: "Start school and make new friends!",
    thumbnailUrl: "/images/templates/school.jpg",
    themeId: "learning",
    styleId: "modern3d",
    ageRange: { min: 3, max: 7 },
    defaultPageCount: 10,
    storyContext: {
      setting: "A welcoming school with friendly teachers and new classmates",
      plotOutline: "Nervous about first day, discovers school is fun, and makes a new friend",
      additionalComments: `COSTUME: New school outfit carefully chosen - neat clothes in favorite color, new backpack with fun design, new shoes, name tag on first day. Expression transitions from nervous to confident through story.
FOR MULTIPLE CHARACTERS: Same neat style, different outfit colors - first in blue, second in red, third in green, fourth in yellow, fifth in purple. Each has unique backpack design.

SCHOOL SUPPLIES: Colorful backpack filled with supplies, new pencil case with crayons, lunchbox with favorite lunch, name tag sticker, cubby with name.

EPIC SCENES: Waking up nervous on big day, getting dressed in new outfit carefully, brave wave goodbye at school door, welcoming teacher greeting with warm smile, finding name on cubby, meeting nice classmate with shy hello, fun learning activity with engaged expression, recess play on playground, eating lunch making conversation, art project with proud creation, happy wave to parent at pickup, excited to tell about new friend.

ENVIRONMENT: Welcoming school building with colorful entrance, cheerful classroom with decorations, cubbies with name tags, circle time carpet, playground with equipment, cafeteria with small tables, art room with supplies.

TONE: Overcoming first-day nervousness. Welcoming school atmosphere. Joy of learning. Excitement of making new friend. Growing confidence through day.`
    }
  },

  {
    id: 'holiday-adventure',
    category: 'occasions',
    occasion: 'christmas',
    titleSingular: "[[NAME]]'s Holiday Adventure",
    titlePlural: "[[NAMES]]'s Holiday Adventure",
    description: "Experience the magic and joy of the holiday season!",
    thumbnailUrl: "/images/templates/holiday.jpg",
    themeId: "celebration",
    styleId: "modern3d",
    ageRange: { min: 3, max: 8 },
    defaultPageCount: 10,
    storyContext: {
      setting: "A winter wonderland during the magical holiday season",
      plotOutline: "Experiences holiday magic, learns about giving, and has a heartwarming celebration",
      additionalComments: `COSTUME: Cozy winter holiday outfit with warm sweater featuring festive pattern, soft scarf in holiday colors, winter boots, mittens. Rosy cheeks from cold. Changes to cozy pajamas for indoor scenes.
FOR MULTIPLE CHARACTERS: Same cozy style, different sweater colors - first in red, second in green, third in gold, fourth in blue, fifth in purple. Each has matching mittens.

HOLIDAY ELEMENTS: Decorated tree with twinkling lights, wrapped presents with colorful ribbons, warm cocoa with marshmallows, snowflakes falling gently, cozy fireplace with stockings, holiday treats and cookies.

EPIC SCENES: Waking to fresh snowfall outside window, decorating tree with ornaments, making holiday cookies with frosting, wrapping presents with careful bows, helping neighbor carry groceries (giving theme), singing carols with rosy cheeks, hot cocoa by fireplace, reading holiday story, magical holiday morning unwrapping gifts, grateful family hug, playing with new toy or gift.

ENVIRONMENT: Snow-covered neighborhood with twinkling lights, cozy home with decorated tree, warm kitchen for baking, neighbor's house for helping, fireplace setting for quiet moments, holiday morning living room.

TONE: Cozy holiday warmth. Joy of giving to others. Family togetherness. Magical holiday atmosphere. Gratitude and love.`
    }
  },

  {
    id: 'new-sibling',
    category: 'occasions',
    occasion: 'new-sibling',
    titleSingular: "[[NAME]] Becomes a Big Sibling",
    titlePlural: "[[NAMES]] Welcome the New Baby",
    description: "Get ready for a new baby and discover the best job in the world: big sibling!",
    thumbnailUrl: "/images/templates/new-sibling.jpg",
    themeId: "celebration",
    styleId: "modern3d",
    ageRange: { min: 2, max: 8 },
    defaultPageCount: 10,
    storyContext: {
      setting: "A cozy family home getting ready for a new baby, from the nursery to the hospital visit and back home",
      plotOutline: "Wonders what the new baby will change, helps prepare the nursery, meets the baby for the first time, and discovers a special big-sibling role",
      additionalComments: `COSTUME: Comfy everyday outfit in a favorite color with a bright "Big Sibling" badge pinned on, soft sneakers. Expression moves from curious and a little unsure to proud and gentle.
FOR MULTIPLE CHARACTERS: Same comfy style, different colors - first in yellow, second in teal, third in coral, fourth in lavender, fifth in green. Every older child wears a matching "Big Sibling" badge; adults wear simple casual clothes.

BABY: A tiny newborn swaddled in a soft pale blanket with a little knit hat, sleeping peacefully or giving a tiny yawn. The baby never does anything a newborn cannot do.

EPIC SCENES: Feeling the baby kick for the first time with wide-eyed wonder, painting a picture for the nursery wall, choosing a favorite old toy to give the baby, helping build the crib with a toy screwdriver, a nervous walk down the hospital hallway, the first look at the baby in a parent's arms, a tiny hand curling around one finger, singing a quiet song by the cradle, a proud wave from the window, a cozy sofa cuddle with the baby at the end.

ENVIRONMENT: Sunny family living room, a pastel nursery with a mobile of stars, a calm hospital room with flowers by the window, the front door with a "Welcome Home" banner, a soft evening sofa scene.

TONE: Gentle reassurance that love grows bigger, not smaller. Curiosity, pride and tenderness. Accepts mixed feelings and ends with a strong sense of belonging.`
    }
  },

  {
    id: 'grandparents-day',
    category: 'occasions',
    occasion: 'grandparents',
    titleSingular: "[[NAME]]'s Big Day at Grandma and Grandpa's",
    titlePlural: "[[NAMES]]'s Big Day at Grandma and Grandpa's",
    description: "Spend a magical day with grandparents, full of stories, baking and old family treasures!",
    thumbnailUrl: "/images/templates/grandparents.jpg",
    themeId: "celebration",
    styleId: "modern3d",
    ageRange: { min: 2, max: 10 },
    defaultPageCount: 10,
    storyContext: {
      setting: "Grandma and Grandpa's warm home with a flower garden, an attic full of family treasures and a kitchen that smells of baking",
      plotOutline: "Arrives for a special visit, discovers an old treasure box in the attic, hears the stories behind the old treasures, and goes home with a keepsake and a promise to come back",
      additionalComments: `COSTUME: Favorite weekend outfit - soft knit sweater in a warm color, comfy trousers, and a little apron for the baking scenes. Grandparents wear cozy cardigans and reading glasses.
FOR MULTIPLE CHARACTERS: Same weekend style, different sweater colors - first in mustard, second in sky blue, third in rose, fourth in sage green, fifth in plum. If grandparents are characters from the photo, they keep their own look from the photo with a cardigan and glasses.

FAMILY TREASURES: A wooden treasure box with a brass latch, an old photo album, a medal, a seashell from a long-ago trip, a knitted scarf, a handwritten recipe card.

EPIC SCENES: Running up the garden path into a big hug, climbing the creaky attic stairs, opening the treasure box in a beam of dusty light, flipping through a photo album of Grandma and Grandpa as children, planting seeds in the garden, baking Grandma's famous cookies with flour on the nose, a picnic under the old apple tree, learning an old card game, a bedtime story in Grandpa's armchair, a goodbye wave holding a new treasure to keep.

ENVIRONMENT: Brick cottage with a flower garden, sunny kitchen with a big wooden table, attic with beams and sunlight, cozy living room with an armchair and a crackling fire.

TONE: Warm, nostalgic and loving. Celebrates the bond between generations, family stories and simple joys. Ends with a keepsake and a promise to visit again.`
    }
  },

  {
    id: 'bedtime-moon',
    category: 'occasions',
    occasion: 'bedtime',
    titleSingular: "[[NAME]] and the Sleepy Moon",
    titlePlural: "[[NAMES]] and the Sleepy Moon",
    description: "A calm, cozy journey to the Moon's dreamland that ends snug in bed.",
    thumbnailUrl: "/images/templates/bedtime.jpg",
    themeId: "fantasy",
    styleId: "modern3d",
    ageRange: { min: 1, max: 7 },
    defaultPageCount: 10,
    storyContext: {
      setting: "A quiet bedroom at night that opens onto a gentle dreamland of clouds, stars and a friendly sleepy Moon",
      plotOutline: "Can't fall asleep, follows a moonbeam to visit the sleepy Moon, helps tuck the little stars in, and floats home ready for sleep",
      additionalComments: `COSTUME: Soft pajamas with a small star pattern in a favorite color, fuzzy socks, a well-loved plush toy held close. Calm expressions that get drowsier page by page.
FOR MULTIPLE CHARACTERS: Same pajama style, different colors - first in midnight blue, second in lavender, third in mint, fourth in peach, fifth in pale yellow. Each holds a different plush toy.

MOON FRIEND: The Moon is a large glowing crescent with a kind, sleepy face and a nightcap, and speaks softly. Little stars have tiny glowing faces and yawn.

EPIC SCENES: Lying awake looking at the window, a silver moonbeam sliding across the blanket, stepping onto the moonbeam with the plush toy, floating past quiet rooftops, landing on a soft cloud, meeting the yawning Moon, gently tucking little stars into cloud blankets, a slow lullaby from the Moon, drifting back down the moonbeam, snuggled in bed with eyes closing and a smile.

ENVIRONMENT: Dim bedroom with a night-light, a sleeping town under a starry sky, pillowy clouds in soft purples and blues, the Moon's cozy crescent home. Low contrast, soft light, no loud action.

TONE: Slow, soothing and repetitive like a lullaby. Short sentences, gentle rhythm, quiet wonder. Every page is calmer than the last; ends with sleep.`
    }
  },

  {
    id: 'diwali-lights',
    category: 'occasions',
    occasion: 'diwali',
    titleSingular: "[[NAME]]'s Festival of Lights",
    titlePlural: "[[NAMES]]'s Festival of Lights",
    description: "Light diyas, draw rangoli and share sweets in a joyful Diwali celebration!",
    thumbnailUrl: "/images/templates/diwali.jpg",
    themeId: "celebration",
    styleId: "modern3d",
    ageRange: { min: 3, max: 10 },
    defaultPageCount: 10,
    storyContext: {
      setting: "A family home and neighborhood glowing with clay lamps, string lights and colorful rangoli on Diwali night",
      plotOutline: "Helps get the home ready for Diwali, creates a rangoli, learns why lamps are lit for light over darkness, and shares sweets and light with a neighbor",
      additionalComments: `COSTUME: New festive clothes for Diwali - a silk kurta or a lehenga in a jewel color with gold embroidery, embroidered juttis (slippers). Hands sometimes holding a small clay diya.
FOR MULTIPLE CHARACTERS: Same festive style, different jewel colors - first in marigold orange, second in peacock blue, third in magenta, fourth in emerald, fifth in deep red. Adults wear a festive kurta or saree in a matching jewel tone.

DIWALI ELEMENTS: Small clay diyas with steady flames lined along steps and windowsills, a rangoli pattern of colored powder and flower petals at the doorway, marigold garlands, paper lanterns, a plate of sweets such as ladoos and barfi, a sparkler held at arm's length with an adult beside the child.

EPIC SCENES: Waking up to the smell of sweets cooking, hanging marigold garlands over the door, drawing a rangoli with colored powder, placing the first diya on the doorstep at dusk, the street lighting up lamp by lamp, admiring the new clothes in the mirror, sharing a box of sweets with a neighbor, a safe sparkler with a grown-up, looking up at the glowing home, a happy moment under the lanterns.

ENVIRONMENT: Home with a lamp-lined balcony and doorway, colorful rangoli at the entrance, warm kitchen with sweets, a neighborhood street of glowing windows at night.

TONE: Joyful, warm and respectful. Light over darkness, kindness and sharing. Celebrates kindness and sharing without depicting deities. Fire is always handled safely with an adult nearby.`
    }
  },

  {
    id: 'mothers-day-surprise',
    category: 'occasions',
    occasion: 'mothers-day',
    titleSingular: "[[NAME]]'s Mother's Day Surprise",
    titlePlural: "[[NAMES]]'s Mother's Day Surprise",
    description: "Plan the perfect surprise to show Mom how much she is loved!",
    thumbnailUrl: "/images/templates/mothers-day.jpg",
    themeId: "celebration",
    styleId: "modern3d",
    ageRange: { min: 2, max: 10 },
    defaultPageCount: 10,
    storyContext: {
      setting: "A family home and garden on Mother's Day morning",
      plotOutline: "Secretly plans a surprise for Mom, makes breakfast, a handmade card and a flower bouquet, laughs off a small kitchen mishap, and gives Mom the gift of a perfect day",
      additionalComments: `COSTUME: Bright spring outfit in a favorite color with a little chef's apron for the breakfast scenes. If Mom is a character from the photo, she wears a comfy robe at breakfast and a sunny dress later.
FOR MULTIPLE CHARACTERS: Same spring style, different colors - first in pink, second in sunflower yellow, third in sky blue, fourth in mint, fifth in lilac. Each child wears a matching apron.

SURPRISE ELEMENTS: A tray with heart-shaped pancakes, orange juice and a flower in a small vase, a handmade card with glitter and a crayon drawing of Mom, a bouquet of garden flowers tied with ribbon, a paper crown that says "Best Mom".

EPIC SCENES: Tiptoeing past Mom's door at sunrise, whispering the secret plan, mixing pancake batter with flour clouds everywhere, a funny pancake flip that lands on the floor, trying again and getting it right, drawing the card at the kitchen table, picking flowers in the garden, carrying the breakfast tray carefully, Mom waking up to the surprise with a huge smile, a big hug and an afternoon walk in the park.

ENVIRONMENT: Quiet hallway at sunrise, sunny kitchen with a mess of flour, colorful spring garden, Mom's bedroom in morning light, a leafy park.

TONE: Sweet, funny and heartfelt. Gratitude and love for Mom. Small mistakes are part of the fun. Ends with a warm hug and a happy heart.`
    }
  },

  {
    id: 'fathers-day-adventure',
    category: 'occasions',
    occasion: 'fathers-day',
    titleSingular: "[[NAME]]'s Father's Day Adventure",
    titlePlural: "[[NAMES]]'s Father's Day Adventure",
    description: "Turn Dad's special day into a backyard adventure he will never forget!",
    thumbnailUrl: "/images/templates/fathers-day.jpg",
    themeId: "celebration",
    styleId: "modern3d",
    ageRange: { min: 2, max: 10 },
    defaultPageCount: 10,
    storyContext: {
      setting: "A family home, backyard and nearby lake on Father's Day",
      plotOutline: "Plans a Father's Day surprise, builds a backyard treasure trail for Dad, goes on a lakeside picnic, and gives Dad a handmade trophy for World's Best Dad",
      additionalComments: `COSTUME: Adventure outfit - cargo shorts or trousers, a T-shirt in a favorite color, a cap worn backwards, binoculars on a strap. If Dad is a character from the photo, he wears a plaid shirt and a matching cap.
FOR MULTIPLE CHARACTERS: Same adventure style, different T-shirt colors - first in red, second in navy, third in forest green, fourth in orange, fifth in teal. Each wears a different cap.

SURPRISE ELEMENTS: A cardboard "World's Best Dad" trophy painted gold, a hand-drawn treasure map of the backyard, a picnic basket with sandwiches, a fishing rod and a little tackle box, a homemade card with a drawing of Dad.

EPIC SCENES: Sneaking out early to set up the surprise, painting the cardboard trophy, hiding clues around the backyard, Dad following the treasure map with a big grin, crossing a pretend rope bridge over the garden, a picnic by the lake, catching a tiny fish and letting it go, skipping stones across the water, presenting the trophy at sunset, a big bear hug.

ENVIRONMENT: Backyard with trees and a garden shed, sunny lakeside with a wooden dock, a picnic blanket on the grass, golden sunset over the water.

TONE: Playful, adventurous and loving. Celebrates time with Dad and the fun of making something by hand. Ends with pride and a big hug.`
    }
  },

  {
    id: 'kindness-counts',
    category: 'educational',
    titleSingular: "[[NAME]] Learns Kindness Counts",
    titlePlural: "[[NAMES]] Learn Kindness Counts",
    description: "Discover how small acts of kindness make a big difference!",
    thumbnailUrl: "/images/templates/kindness.jpg",
    themeId: "learning",
    styleId: "modern3d",
    ageRange: { min: 3, max: 7 },
    defaultPageCount: 10,
    storyContext: {
      setting: "Everyday settings - home, school, neighborhood - where kindness matters",
      plotOutline: "Learns about kindness, performs acts of kindness, and sees how they spread",
      additionalComments: `COSTUME: Relatable everyday outfit with comfortable casual clothes, friendly approachable appearance. Expressions show compassion, thoughtfulness, and joy throughout kindness journey.
FOR MULTIPLE CHARACTERS: Same casual style, different shirt colors - first in yellow (sunny and kind), second in blue, third in pink, fourth in green, fifth in orange.

KINDNESS SYMBOLS: Hearts appearing when kind acts happen, chain of kindness connecting people, thankful expressions on recipients, ripple effects shown visually, kindness jar collecting acts.

EPIC SCENES: Noticing someone who needs help, choosing to offer assistance with warm smile, helping classmate with dropped books, sharing toy with lonely peer, making card for sad friend, helping parent with chores unprompted, letting sibling go first, including new kid at playground, seeing recipient pay kindness forward, kindness chain connecting whole community, proud feeling at day's end.

ENVIRONMENT: School classroom and hallways, playground during recess, home settings for family kindness, neighborhood streets, park where community gathers.

TONE: Gentle teaching through example. Warm fuzzy feelings from helping. Observation of kindness spreading. Pride in making difference. Understanding that small acts matter.`
    }
  },

  {
    id: 'feelings-adventure',
    category: 'educational',
    titleSingular: "[[NAME]]'s Feelings Adventure",
    titlePlural: "[[NAMES]]'s Feelings Adventure",
    description: "Explore emotions and learn healthy ways to express them!",
    thumbnailUrl: "/images/templates/feelings.jpg",
    themeId: "learning",
    styleId: "modern3d",
    ageRange: { min: 3, max: 7 },
    defaultPageCount: 10,
    storyContext: {
      setting: "Everyday situations that bring up different emotions",
      plotOutline: "Experiences various emotions throughout a day and learns healthy ways to handle each one",
      additionalComments: `COSTUME: Comfortable everyday clothes that allow full expression of emotions through body language. Face expressions are central - each page focuses on a different clear emotion shown on face.
FOR MULTIPLE CHARACTERS: Same casual style, different colored shirts - first in orange, second in blue, third in purple, fourth in green, fifth in red. Each shows emotions clearly.

EMOTION VISUALIZATION: Feelings shown clearly through facial expressions - big smile for happy, tears for sad, red cheeks and fists for angry, wide eyes for scared, relaxed face for calm. Environment colors can reflect mood (warm yellows for happy, blues for sad).

EPIC SCENES: Happy moment playing favorite activity, sad moment when toy breaks, angry moment when something unfair happens, scared moment in dark, calm moment after taking deep breaths, excited moment receiving good news, proud moment accomplishing something, loved moment in family hug. Each emotion named and normalized. Coping strategies shown - deep breaths, talking about feelings, asking for hug.

ENVIRONMENT: Home settings for different emotional moments, playground for happy and conflict moments, bedroom for private emotions, family spaces for support scenes.

TONE: All feelings are valid and normal. Healthy expression is good. Asking for help is strong. Feelings pass and change. Love and support help us cope.`
    }
  },

  // ============================================
  // SPECIAL/OTHER TEMPLATES
  // ============================================
  {
    id: 'environmental-hero',
    category: 'educational',
    titleSingular: "[[NAME]] the Environmental Hero",
    titlePlural: "[[NAMES]] the Environmental Heroes",
    description: "Protect the planet and become an eco-warrior!",
    thumbnailUrl: "/images/templates/environmental.jpg",
    themeId: "nature",
    styleId: "modern3d",
    ageRange: { min: 4, max: 10 },
    defaultPageCount: 10,
    storyContext: {
      setting: "Community and nature environments that need protection and care",
      plotOutline: "Learns about environmental problems, takes action to help, and inspires others to join",
      additionalComments: `COSTUME: Eco-warrior outfit with green t-shirt featuring Earth design, comfortable shorts or pants made from recycled materials, gardening gloves for hands-on work, reusable water bottle, cloth bag for collecting recyclables.
FOR MULTIPLE CHARACTERS: Same eco-warrior style, different earth-tone shirts - first in green, second in brown, third in blue (water), fourth in orange (sun), fifth in purple (flowers).

ENVIRONMENTAL GEAR: Recycling bins in different colors, gardening tools for planting, trash grabber for cleanups, reusable bags and bottles, seeds and plants, informational signs to share with community.

EPIC SCENES: Noticing litter in beautiful park, determined decision to help, organizing community cleanup with supplies, planting trees with careful digging, starting garden with seeds, teaching younger kids about recycling, setting up bird houses and feeders, before-and-after of cleaned area, giving presentation about saving Earth, receiving thank you from grateful community, looking at healthy nature with pride.

ENVIRONMENT: Local park needing cleanup then beautified, school garden area, recycling center, tree planting location, bird sanctuary, community center for presentation, home with eco-friendly setup.

TONE: Empowerment to make difference. Learning leads to action. Community working together. Pride in environmental stewardship. Hope for planet's future.`
    }
  },

  {
    id: 'start-business',
    category: 'educational',
    titleSingular: "[[NAME]] Starts a Business",
    titlePlural: "[[NAMES]] Start a Business",
    description: "Become a young entrepreneur and start a business!",
    thumbnailUrl: "/images/templates/business.jpg",
    themeId: "learning",
    styleId: "modern3d",
    ageRange: { min: 5, max: 10 },
    defaultPageCount: 10,
    storyContext: {
      setting: "Home and neighborhood where a small business comes to life",
      plotOutline: "Gets a business idea, creates products, and successfully sells to customers",
      additionalComments: `COSTUME: Young entrepreneur professional-casual look with nice polo or button shirt, khaki pants, apron when working on product. Has business name badge and confident posture. Maybe bow tie for extra business flair.
FOR MULTIPLE CHARACTERS: Same professional-casual style, different apron colors - first in blue, second in red, third in green, fourth in yellow, fifth in purple. Each has business role specialty.

BUSINESS SUPPLIES: Handmade products (lemonade, crafts, baked goods), cash register or money box, "Open for Business" sign, price tags, receipt pad, business plan notebook, simple marketing materials.

EPIC SCENES: Getting great business idea with lightbulb moment, making business plan with careful thought, creating product with quality focus, designing sign and setting up stand, nervous opening day preparation, first customer approaching with hopeful expression, first sale with proud smile, busy rush of customers, counting money at end of day, saving money in piggy bank, deciding how to use profits (saving, giving, investing).

ENVIRONMENT: Kitchen or workspace for creating products, driveway or front yard for stand setup, neighborhood with walking customers, local park for expanded location, home for planning and counting.

TONE: Entrepreneurial creativity and initiative. Learning through doing. Problem-solving challenges. Pride in earned income. Financial responsibility lessons.`
    }
  },

  {
    id: 'robot-fairy-woods',
    category: 'fantasy',
    titleSingular: "[[NAME]]'s Robot Fairy Woods",
    titlePlural: "[[NAMES]]'s Robot Fairy Woods",
    description: "Discover a magical forest where robot fairies live!",
    thumbnailUrl: "/images/templates/robot-fairy.jpg",
    themeId: "fantasy",
    styleId: "modern3d",
    ageRange: { min: 4, max: 8 },
    defaultPageCount: 10,
    storyContext: {
      setting: "A magical forest where technology and nature combine in robot fairies",
      plotOutline: "Discovers a hidden forest, befriends robot fairies, and helps them with a problem",
      additionalComments: `COSTUME: Whimsical forest explorer outfit with cozy sweater in soft purple, comfortable leggings, sturdy but cute boots, flower crown received as gift from robot fairies. Pockets full of collected treasures.
FOR MULTIPLE CHARACTERS: Same cozy style, different sweater colors - first in purple, second in mint green, third in soft pink, fourth in sky blue, fifth in sunny yellow. Each receives different flower crown.

ROBOT FAIRY FRIENDS: Adorable mechanical fairies with metallic bodies in rose gold, copper, and silver, LED wings that glow and flutter, antenna with tiny flowers, gear-shaped accents, expressive digital eyes on screen faces, can project holograms and create flower blooms with their magic.

EPIC SCENES: Discovering hidden path into enchanted woods, first glimpse of robot fairy with gasp of wonder, cautious approach and gentle hello, robot fairy projecting hologram to communicate, tour of robot fairy village in giant flowers, meeting robot fairy queen in grand hall, learning robot fairies' problem (broken forest heart machine), working with fairies to fix machine, machine restored with forest blooming, celebration dance with glowing fairies, receiving robot fairy gift and saying goodbye.

ENVIRONMENT: Enchanted forest with oversized mechanical flowers, trees with circuit-pattern bark, robot fairy village in giant tech-flowers, heart of forest with central machine, bioluminescent plants mixed with LED glows.

TONE: Wonder at magical technology. Gentle friendship building. Problem-solving together. Beautiful blend of nature and tech. Bittersweet but hopeful farewell.`
    }
  }
];

// Export count for verification
export const UNIFIED_TEMPLATE_COUNT = UNIFIED_STORY_TEMPLATES.length;

// Helper function to get template by ID
export function getUnifiedTemplate(id: string): UnifiedStoryTemplate | undefined {
  return UNIFIED_STORY_TEMPLATES.find(t => t.id === id);
}

// Helper function to get title based on character count
export function getTemplateTitle(template: UnifiedStoryTemplate, characterCount: number): string {
  return characterCount === 1 ? template.titleSingular : template.titlePlural;
}

/**
 * Fills a plural title: [[NAMES]] gets the joined names; a hero-only plural title ([[NAME]]) gets the hero's name.
 * One pass with a replacer function: names are inserted literally (a "$&" or "$1" in a name is never a replacement
 * pattern, and an inserted name is never re-scanned for placeholders).
 */
export function fillPluralTitle(title: string, joinedNames: string, heroName: string): string {
  return title.replace(/\[\[(NAMES|NAME)\]\]/g, (_m, key: string) => (key === 'NAMES' ? joinedNames : heroName));
}

// Helper function to personalize title with names
export function personalizeTitle(
  template: UnifiedStoryTemplate,
  characters: Array<{ name: string }>,
  heroName?: string
): string {
  const baseTitle = getTemplateTitle(template, characters.length);

  if (characters.length === 1) {
    return baseTitle.replace(/\[\[NAME\]\]/g, () => characters[0].name);
  }

  // Format names for multiple characters
  const names = characters.map(c => c.name);
  let formattedNames: string;

  if (names.length === 2) {
    formattedNames = `${names[0]} and ${names[1]}`;
  } else {
    formattedNames = names.slice(0, -1).join(', ') + ', and ' + names[names.length - 1];
  }

  return fillPluralTitle(baseTitle, formattedNames, heroName ?? names[0]);
}

// Helper function to filter out "FOR MULTIPLE CHARACTERS" sections for single-character stories
// This prevents the AI from seeing multi-character instructions when only one character exists
export function filterCommentsForCharacterCount(comments: string, characterCount: number): string {
  if (characterCount > 1) {
    // Keep everything for multi-character stories
    return comments;
  }

  // For single-character stories, remove all "FOR MULTIPLE CHARACTERS:" lines
  // These lines start with "FOR MULTIPLE CHARACTERS:" and continue until the next section or double newline
  return comments
    .split('\n')
    .filter(line => !line.trim().startsWith('FOR MULTIPLE CHARACTERS:'))
    .join('\n')
    // Clean up any resulting double blank lines
    .replace(/\n{3,}/g, '\n\n')
    .trim();
}

// Helper function to get templates by category
export function getTemplatesByCategory(category: UnifiedStoryTemplate['category']): UnifiedStoryTemplate[] {
  return UNIFIED_STORY_TEMPLATES.filter(template => template.category === category);
}

// Helper function to get templates suitable for a specific age
export function getTemplatesForAge(age: number): UnifiedStoryTemplate[] {
  return UNIFIED_STORY_TEMPLATES.filter(
    template => age >= template.ageRange.min && age <= template.ageRange.max
  );
}

// Story length options with descriptions
export const STORY_LENGTH_OPTIONS = [
  {
    pages: 8,
    label: 'Quick Read (8 pages)',
    description: 'Perfect for bedtime stories',
    details: '5-7 minute read',
    price: 'Base price'
  },
  {
    pages: 16,
    label: 'Standard Story (16 pages)',
    description: 'Great balance of story and illustrations',
    details: '10-15 minute read',
    price: '+$8.00',
    recommended: true
  },
  {
    pages: 24,
    label: 'Epic Tale (24 pages)',
    description: 'Complete adventure with multiple story arcs',
    details: '20-25 minute read',
    price: '+$16.00'
  }
];

// Legacy compatibility - export STORY_TEMPLATES as alias
export const STORY_TEMPLATES = UNIFIED_STORY_TEMPLATES;
